import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Typography, Table, Button, Space, Divider, Modal } from 'antd';

import { PageProps } from '@/types/PageProps';
import { ColumnsType } from 'antd/lib/table';
import { ProfileReduced } from '@/core/domain/enterprise/Profiles/Profiles.types';
import {
  useProfilesReduced,
  useRemoveProfile,
} from '@/core/domain/enterprise/Profiles/Profiles.hooks';
import PermissionIcon from '@/components/_common/PermissionIcon';
import { DeleteIcon } from '@/components/_common';
import Authorities from '@/types/Authority';
import AccessDenied from '@/components/_common/AccessDenied';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const { Title } = Typography;

const ProfilesPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { data, isFetching } = useProfilesReduced();

  const removeMutation = useRemoveProfile();

  const handleRemoveClick = (profile: ProfileReduced) => {
    Modal.confirm({
      title: 'Remoção de grupo de usuário',
      content:
        'Deseja realmente excluir o grupo de usuário, esse processo será irreversível',
      onOk: async () => {
        await removeMutation.mutateAsync(profile.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<ProfileReduced> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'paymentConditions.domain.name' }),
      render: (value) => value || '-',
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (_, row) => (
        <Space>
          {hasPermissions([
            Authorities.CONFIGURATION_PROFILE_PERMISSIONS_VIEW,
            Authorities.PROFILE_UPDATE,
            Authorities.CONFIGURATION_MATRIX_MANAGE_USER_GROUPS,
          ]) && (
            <PermissionIcon
              redirect={`/app/settings/profiles/${row.id}`}
              disabled={row.system}
            />
          )}

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={
              !hasPermissions([Authorities.PROFILE_DELETE]) || row.system
            }
          />
        </Space>
      ),
      align: 'right',
      fixed: 'right',
    },
  ];

  if (
    !hasPermissions([
      Authorities.PROFILE_READ,
      Authorities.CONFIGURATION_MATRIX_SETTINGS,
    ])
  ) {
    return <AccessDenied />;
  }

  return (
    <div className="page">
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page-header">
        <Title level={4}>
          <FormattedMessage id={route.name} />
        </Title>

        <Button
          type="primary"
          onClick={() => history.push('/app/settings/profiles/create')}
          disabled={!hasPermissions([Authorities.PROFILE_CREATE])}
        >
          <FormattedMessage id="profiles.create" />
        </Button>
      </div>

      <Divider />

      <Table
        size="middle"
        columns={columns}
        dataSource={data}
        loading={isFetching}
        rowKey={(row) => row.id}
        pagination={false}
      />
    </div>
  );
};

export default ProfilesPage;
